import React, { useState, useEffect } from "react";
import Joyride from "react-joyride";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  Dialog,
  TextField,
  Divider,
  useMediaQuery,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import { customerservice, setupManagementUrl } from "../../../../config";
import { fetchJsonDynamic, executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const CustomerEmployerMappingDialog = ({ open, onClose, Row }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState("");
  const [runTour, setRunTour] = useState(false);

   const steps = [
    {
      target: ".customer-info",
      content: "This section shows the customer you are mapping.",
    },
    {
      target: ".employer-select",
      content: "Select the employer you want to map this customer to.",
    },
    {
      target: ".map-button",
      content: "Click here to confirm the mapping.",
    },
  ];
  useEffect(() => {
    async function fetchEmployers() {
      const response = await fetchJsonDynamic(
        "allEmployers",
        null,
        null,
        setupManagementUrl.uri,
      );
      const parsed = JSON.parse(response?.results || "[]");
      setEmployers(parsed);
    }

    if (open) fetchEmployers();
  }, [open]);

  const handleMappingSubmit = async () => {
    if (!selectedEmployer) {
      Swal.fire("Error", "Please select an employer.", "warning");
      return;
    }

    try {
      onClose(); // close before confirmation to improve UX
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `Map this customer to the selected employer?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, map",
        cancelButtonText: "Cancel",
      });

      if (confirmation.isConfirmed) {
        const mutationData = {
          customerid: Row.Id,
          employeeId: selectedEmployer,
        };

        const response = await executeAnyMutation(
          "mapCustomerToEmployer",
          mutationData,
          "customerEmployer",
          false,
          customerservice.uri
        );

        if (response.results) {
          Swal.fire("Success", "Customer mapped to employer successfully.", "success");
        } else {
          throw new Error("Mapping failed");
        }
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Something went wrong", "error");
    }
  };

  const handleClose = () => {
    setSelectedEmployer("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
       <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        callback={(data) => {
          if (["finished", "skipped"].includes(data.status)) {
            setRunTour(false);
          }
        }}
        styles={{ options: 
          { 
            primaryColor: "#1976d2",
            zIndex: 9999
          } }}
      />
      <IconButton
        sx={{ position: "absolute", right: 8, top: 8, color: theme.palette.grey[500] }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ bgcolor: "background.paper", p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Map Customer to Employer
          <Divider sx={{ borderBottomWidth: 2, border: "1px solid #003049" }} />
          <Button onClick={() => setRunTour(true)}>Start Tour</Button>

        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledCard variant="outlined" className="customer-info">
              <Typography>
                Customer: <strong>{Row?.FirstName} {Row?.OtherNames}</strong>
              </Typography>
            </StyledCard>

            <FormControl fullWidth sx={{ mt: 2 }} className="employer-select">
              <InputLabel id="employer-select-label">Select Employer</InputLabel>
              <Select
                labelId="employer-select-label"
                value={selectedEmployer}
                onChange={(e) => setSelectedEmployer(e.target.value)}
                label="Select Employer"
              >
                {employers.map((emp) => (
                  <MenuItem key={emp.Id} value={emp.Id}>
                    {emp.Employer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleMappingSubmit}
                fullWidth
                className="map-button"
              >
                Map Customer
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};
